import type { AIStyle, Theme } from "@fiket/booth-schema";
import type { Language } from "../../store/router";
import { styleName, themeKicker, themeName, themeThumbnail } from "../../hooks/useStyles";
import { useI18n } from "../../i18n/useI18n";

/** 카드 하단에 보여줄 하위 스타일 미니 썸네일 최대 개수 — 넘치면 "+N" */
const MAX_MINIS = 5;

interface Props {
  theme: Theme;
  /** 카루셀 내 순번(0-base) — 킥커 "NN · CODE" 와 "n / N" 에 쓴다 */
  index: number;
  total: number;
  styles: readonly AIStyle[];
  active: boolean;
  language: Language;
  onSelect: () => void;
}

/**
 * 테마 카드 (HERIT 540×820) — 대표 이미지(73.6%) + 킥커 · 44px 제목 · n/N + "담긴 이야기 · N" 헤어라인 +
 * 하위 스타일 48px 미니 썸네일. 가운데(active) 카드만 선명하고 옆 카드는 CSS 가 흐리게 깔아 둔다.
 */
export function ThemeCard({ theme, index, total, styles, active, language, onSelect }: Props) {
  const { t } = useI18n();
  const name = themeName(theme, language);
  const src = themeThumbnail(theme, styles);
  const subs = styles.filter((s) => s.themeId === theme.id);
  const minis = subs.slice(0, MAX_MINIS);
  const more = subs.length - minis.length;
  return (
    <button
      type="button"
      className="theme-card"
      data-theme-id={theme.id}
      data-active={active || undefined}
      role="option"
      aria-selected={active}
      aria-label={name}
      onClick={onSelect}
    >
      <div className="theme-card__img">
        {src ? (
          <img src={src} alt="" draggable={false} />
        ) : (
          <span className="theme-card__initial" aria-hidden>{name.slice(0, 1)}</span>
        )}
      </div>
      <div className="theme-card__foot">
        <div className="theme-card__head">
          <div>
            <span className="kicker">{themeKicker(theme, index)}</span>
            <h2>{name}</h2>
          </div>
          <span className="kicker theme-card__pos">{index + 1} / {total}</span>
        </div>
        <div className="theme-card__stories">
          <span className="kicker">{t.themeSelect.storiesLabel} · {subs.length}</span>
          <i className="hairline" aria-hidden />
        </div>
        {minis.length > 0 ? (
          <ul className="theme-card__minis">
            {minis.map((s) => (
              <li key={s.id}>
                {s.thumbnailUrl ? <img src={s.thumbnailUrl} alt="" draggable={false} /> : <span className="theme-card__mini-blank" />}
                <span>{styleName(s, language)}</span>
              </li>
            ))}
            {more > 0 ? <li className="theme-card__more"><span className="theme-card__mini-blank">+{more}</span></li> : null}
          </ul>
        ) : null}
      </div>
    </button>
  );
}
